export interface OrdersUrlState {
  startDate: string;
  endDate: string;
  country: string;
  status: string;
  itemsPerPage: string;
  search: string;
  pic: string;
  hasOrderNumber: boolean;
  hasTrackingNumber: boolean;
  currentCursor: string | undefined;
  prevCursors: Array<string | undefined>;
}

type OrdersUrlFallback = Omit<OrdersUrlState, 'search' | 'pic' | 'currentCursor' | 'prevCursors'>;

const defaultPageSize = '15';
const pageSizes = new Set(['10', defaultPageSize, '20', '30', '50', '100', '200']);
const dateOnly = /^\d{4}-\d{2}-\d{2}$/;

const isDateOnly = (value: string | null): value is string => {
  if (!value || !dateOnly.test(value)) {
    return false;
  }

  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

const readPrevCursors = (raw: string | null): Array<string | undefined> => {
  if (!raw) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map((cursor) => (typeof cursor === 'string' ? cursor : undefined));
  } catch {
    return [];
  }
};

export const readOrdersUrlState = (params: URLSearchParams, fallback: OrdersUrlFallback): OrdersUrlState => {
  const urlLimit = params.get('limit');
  const urlStatus = params.get('status');
  const urlCountry = params.get('country');
  const urlStartDate = params.get('startDate');
  const urlEndDate = params.get('endDate');
  const fallbackLimit = pageSizes.has(fallback.itemsPerPage) ? fallback.itemsPerPage : defaultPageSize;
  const urlDates =
    isDateOnly(urlStartDate) && isDateOnly(urlEndDate)
      ? { startDate: urlStartDate, endDate: urlEndDate }
      : { startDate: fallback.startDate, endDate: fallback.endDate };

  return {
    startDate: urlDates.startDate,
    endDate: urlDates.endDate,
    country: urlCountry === 'Select Country' ? '' : urlCountry || fallback.country,
    status: urlStatus && /^[a-z-]+$/.test(urlStatus) ? urlStatus : params.has('status') ? '' : fallback.status,
    itemsPerPage: urlLimit && pageSizes.has(urlLimit) ? urlLimit : fallbackLimit,
    search: params.get('search') || '',
    pic: params.get('pic') || '',
    hasOrderNumber: params.has('hasOrderNumber') ? params.get('hasOrderNumber') === 'true' : fallback.hasOrderNumber,
    hasTrackingNumber: params.has('hasTrackingNumber')
      ? params.get('hasTrackingNumber') === 'true'
      : fallback.hasTrackingNumber,
    currentCursor: params.get('cursor') || undefined,
    prevCursors: readPrevCursors(params.get('prevCursors')),
  };
};

export const buildOrdersUrlParams = (state: OrdersUrlState): URLSearchParams => {
  const params = new URLSearchParams();

  params.set('startDate', state.startDate);
  params.set('endDate', state.endDate);
  params.set('limit', state.itemsPerPage);

  if (state.country) {
    params.set('country', state.country);
  }
  if (state.status) {
    params.set('status', state.status);
  }
  if (state.search.trim()) {
    params.set('search', state.search.trim());
  }
  if (state.pic.trim()) {
    params.set('pic', state.pic.trim());
  }
  if (state.hasOrderNumber) {
    params.set('hasOrderNumber', 'true');
  }
  if (state.hasTrackingNumber) {
    params.set('hasTrackingNumber', 'true');
  }
  if (state.currentCursor) {
    params.set('cursor', state.currentCursor);
  }
  if (state.prevCursors.length > 0) {
    params.set('prevCursors', JSON.stringify(state.prevCursors));
  }

  return params;
};
